/**
 * IXAM logo — coordinated with the Grenoble / GreenApp design system.
 *
 * Mark: rounded-square tile in forest-green gradient (#52B788 → #2D6A4F)
 * with a deep-ink "X" inside (cross-vendor monitoring) and a small red
 * pulse indicator (incoming alerts).
 *
 * Wordmark: Inter 800 — same hierarchy as Grenoble brand titles.
 */

interface LogoProps {
  /** Square size of the mark in px. Wordmark scales relative to it. */
  size?: number;
  /** "full" = mark + wordmark side by side; "stacked" = stacked. */
  variant?: 'full' | 'mark' | 'stacked';
  /** Use the dark-on-dark variant (white wordmark, light tagline). */
  onDark?: boolean;
  className?: string;
  withTagline?: boolean;
}

export function Logo({
  size = 32,
  variant = 'full',
  onDark = false,
  className = '',
  withTagline = false,
}: LogoProps) {
  // IXAM: dimensiona wordmark e tagline in base alla SQUARE size del mark,
  // ma usando proporzioni Grenoble (title 100%, small ~73%) e Inter classes
  // del resto dell'app (no uppercase, no tracking largo) per coerenza visiva.
  const wordmarkSize = Math.round(size * 0.5);
  const taglineSize = Math.max(11, Math.round(wordmarkSize * 0.7));

  const wordmarkColor = onDark ? 'text-white' : 'text-ink';
  const taglineColor = onDark ? 'text-accent-on-dark-faint' : 'text-ink-muted';

  const mark = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="IXAM logo"
    >
      <defs>
        <linearGradient id="ixamBrand" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#52B788" />
          <stop offset="100%" stopColor="#2D6A4F" />
        </linearGradient>
        <linearGradient id="ixamShine" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="white" stopOpacity="0.22" />
          <stop offset="60%" stopColor="white" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect x="3" y="3" width="42" height="42" rx="11" fill="url(#ixamBrand)" />
      <rect x="3" y="3" width="42" height="42" rx="11" fill="url(#ixamShine)" />
      <path
        d="M16 16 L32 32 M32 16 L16 32"
        stroke="#0F2A1F"
        strokeWidth="3.6"
        strokeLinecap="round"
      />
      <circle cx="38" cy="10" r="6" fill="#D95F5F" opacity="0.22" />
      <circle cx="38" cy="10" r="3.6" fill="#D95F5F" />
      <circle cx="38" cy="10" r="1.4" fill="#FFFFFF" />
    </svg>
  );

  if (variant === 'mark') {
    return <div className={className}>{mark}</div>;
  }

  if (variant === 'stacked') {
    return (
      <div className={`flex flex-col items-center ${className}`}>
        {mark}
        <div
          className={`font-extrabold ${wordmarkColor} leading-none mt-3 tracking-tight`}
          style={{ fontSize: `${wordmarkSize}px` }}
        >
          IXAM
        </div>
        {withTagline && (
          <div
            className={`${taglineColor} font-medium mt-1.5`}
            style={{ fontSize: `${taglineSize}px` }}
          >
            Monitor alert hardware
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {mark}
      <div className="leading-tight min-w-0">
        <div
          className={`font-extrabold ${wordmarkColor} tracking-tight`}
          style={{ fontSize: `${wordmarkSize}px` }}
        >
          IXAM
        </div>
        {withTagline && (
          <div
            className={`${taglineColor} font-medium mt-1`}
            style={{ fontSize: `${taglineSize}px` }}
          >
            Monitor alert hardware
          </div>
        )}
      </div>
    </div>
  );
}
